import { useEffect, useMemo, useRef, useState } from "react";
import { calculateTimerProgress } from "../utils/timerUtils";

export interface UseTimerReturn {
  secondsLeft: number;
  progress: number;
  isActive: boolean;
}

/**
 * Custom hook for managing countdown timer
 * @param initialSeconds - Initial seconds to countdown from, or undefined to disable
 * @param enabled - Whether the timer should be active
 * @param onComplete - Optional callback when timer reaches 0
 * @param createdAt - ISO timestamp when the timer was created. Combined with totalSeconds to derive
 *   an absolute expiry time so the countdown stays accurate even when the tab is backgrounded,
 *   the device sleeps, or the browser is closed and reopened.
 * @param totalSeconds - Total duration in seconds (required when createdAt is supplied)
 * @returns Object containing secondsLeft, progress (0-100), and isActive status
 */
export const useTimer = (
  initialSeconds: number | undefined,
  enabled: boolean,
  onComplete?: () => void,
  createdAt?: Date | string,
  totalSeconds?: number
): UseTimerReturn => {
  // Derive absolute expiry epoch ms from createdAt + totalSeconds when both are available.
  // Using an absolute time means the countdown is always computed from the wall clock, so
  // background throttling, sleep, or a closed browser cannot stall it.
  const expiresAt = useMemo(() => {
    if (!createdAt || !totalSeconds) return undefined;
    return new Date(createdAt).getTime() + totalSeconds * 1000;
  }, [createdAt, totalSeconds]);

  const durationSeconds = totalSeconds ?? initialSeconds ?? 1;

  // Stable ref so interval/visibility callbacks always call the latest onComplete
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const hasCalledCompleteRef = useRef(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const computeSecondsLeft = (): number => {
    if (expiresAt !== undefined) {
      return Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
    }
    return initialSeconds ?? 0;
  };

  const [secondsLeft, setSecondsLeft] = useState<number>(computeSecondsLeft);
  const [progress, setProgress] = useState(0);

  // Reset state whenever the timer definition changes
  useEffect(() => {
    const secs =
      expiresAt !== undefined
        ? Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
        : (initialSeconds ?? 0);
    setSecondsLeft(secs);
    setProgress(0);
    hasCalledCompleteRef.current = false;
  }, [expiresAt, initialSeconds]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (!enabled) return;

    // Neither an absolute expiry nor an initial duration — caller intentionally disabled the timer
    if (expiresAt === undefined && initialSeconds === undefined) return;

    const currentSecs =
      expiresAt !== undefined
        ? Math.max(0, Math.floor((expiresAt - Date.now()) / 1000))
        : (initialSeconds ?? 0);

    // Already expired before the timer even started
    if (currentSecs <= 0) {
      if (!hasCalledCompleteRef.current) {
        hasCalledCompleteRef.current = true;
        onCompleteRef.current?.();
      }
      return;
    }

    const tick = () => {
      if (expiresAt !== undefined) {
        // Wall-clock path: always accurate regardless of tab visibility
        const secs = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
        setSecondsLeft(secs);
        setProgress(
          Math.max(
            0,
            Math.min(100, calculateTimerProgress(secs, durationSeconds))
          )
        );
        if (secs <= 0 && !hasCalledCompleteRef.current) {
          hasCalledCompleteRef.current = true;
          onCompleteRef.current?.();
          if (timerRef.current) clearInterval(timerRef.current);
        }
      } else {
        // Legacy path: decrement by 1 per tick (no createdAt available)
        setSecondsLeft((prev) => {
          const newSecs = prev - 1;
          if (initialSeconds !== undefined) {
            setProgress(
              Math.max(
                0,
                Math.min(100, calculateTimerProgress(newSecs, initialSeconds))
              )
            );
          }
          if (newSecs <= 0 && !hasCalledCompleteRef.current) {
            hasCalledCompleteRef.current = true;
            onCompleteRef.current?.();
            return 0;
          }
          return Math.max(0, newSecs);
        });
      }
    };

    timerRef.current = setInterval(tick, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, expiresAt, initialSeconds, durationSeconds]);

  // Immediately resync to wall clock when the page becomes visible again.
  // This fires after the device wakes, the user switches back to the tab, etc.
  useEffect(() => {
    if (!enabled || expiresAt === undefined) return;

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const secs = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setSecondsLeft(secs);
      setProgress(
        Math.max(
          0,
          Math.min(100, calculateTimerProgress(secs, durationSeconds))
        )
      );
      if (secs <= 0 && !hasCalledCompleteRef.current) {
        hasCalledCompleteRef.current = true;
        onCompleteRef.current?.();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [enabled, expiresAt, durationSeconds]);

  return {
    secondsLeft,
    progress,
    isActive: enabled && secondsLeft > 0,
  };
};
